import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';

import CostSelect from "./CostSelect";
import TypeSelect from "./TypeSelect";
import SummarySelect from "./SummarySelect";
import PlaceSelect from "./PlaceSelect";

import {VARIABLES, DISPLAYS, COSTS, SUMMARIES} from '../../utilities/constants';
import {TEXT} from '../../utilities/strings';

/**
 * Component for drawing the main control form
 *
 * @example
 * return (
 * <ControlForm
 *   formName={sectionType}
 *   showSelectedCost={true}
 *   showSelectedState={true}
 *   showSelectedCounty={true}
 *   showComparisonCounty={true}
 *   showDisplayType={true}
 *   countiesData={this.props.countiesData}
 *   statesData={this.props.statesData}
 *   displayType={this.props.displayType}
 *   summaryType={this.props.summaryType}
 *   selectedCost={this.props.selectedCost}
 *   selectedStateFips={this.props.selectedStateFips}
 *   comparisonStateFips={this.props.comparisonStateFips}
 *   selectedCountyFips={this.props.selectedCountyFips}
 *   comparisonCountyFips={this.props.comparisonCountyFips}
 *   onSubmit={this.props.onUpdateSelections}
 *  />
 *  )
 */
export default class ControlForm extends PureComponent {

  static propTypes = {
    statesData: PropTypes.array,
    countiesData: PropTypes.array,
    showSelectedState: PropTypes.bool,
    showComparisonState: PropTypes.bool,
    showSelectedCounty: PropTypes.bool,
    showComparisonCounty: PropTypes.bool,
    showSelectedCost: PropTypes.bool,
    showDisplayType: PropTypes.bool,
    showSummaryType: PropTypes.bool,
    summaryType: PropTypes.string,
    displayType: PropTypes.string,
    selectedCost: PropTypes.string,
    selectedStateFips: PropTypes.number,
    comparisonStateFips: PropTypes.number,
    selectedCountyFips: PropTypes.number,
    comparisonCountyFips: PropTypes.number,
    focusIdOnSubmit: PropTypes.string,
    formName: PropTypes.string.isRequired,
    onSubmit: PropTypes.func.isRequired
  };

  state = {
    displayType: this.props.displayType,
    summaryType: this.props.summaryType,
    selectedCost: this.props.selectedCost,
    selectedStateFips: this.props.selectedStateFips,
    comparisonStateFips: this.props.comparisonStateFips,
    selectedCountyFips: this.props.selectedCountyFips,
    comparisonCountyFips: this.props.comparisonCountyFips
  };

  /**
   * Sets state for each property on update
   */

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (!_.isEqual(prevProps, this.props)) {
      this.setState({
        displayType: this.props.displayType,
        summaryType: this.props.summaryType,
        selectedCost: this.props.selectedCost,
        selectedStateFips: this.props.selectedStateFips,
        comparisonStateFips: this.props.comparisonStateFips,
        selectedCountyFips: this.props.selectedCountyFips,
        comparisonCountyFips: this.props.comparisonCountyFips,
      })
    }
  }

  /**
   * Resets data in the control form to defaults
   */

  handleReset = () => {
    this.setState({
      displayType: DISPLAYS.CHART,
      summaryType: SUMMARIES.SUMMARY,
      selectedCost: COSTS.TOTAL,
      selectedStateFips: null,
      comparisonStateFips: null,
      selectedCountyFips: null,
      comparisonCountyFips: null
    })
  };

  handleSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.props.focusIdOnSubmit) {
      this.findAndFocusElement(this.props.focusIdOnSubmit);
    }

    this.props.onSubmit(_.cloneDeep(this.state));
  };

  findAndFocusElement = (elementId) => {
    const element = window.document.getElementById(elementId);

    if (element && element.focus) {
      element.focus({preventScroll: true})
    }
  }

  render() {

    return (
      <Form onSubmit={this.handleSubmit} aria-label={`${this.props.formName} section controls`}>

        {this.props.showSelectedCost && (
          <CostSelect
            label={TEXT.cost_type + ':'}
            formId="selectedCost"
            formName={this.props.formName}
            value={this.state.selectedCost}
            onChange={value => this.setState({selectedCost: value})}
          />
        )}

        {this.props.showSelectedState && (
          <PlaceSelect
            label={TEXT.selected_state + ':'}
            formId="selectedStateFips"
            formName={this.props.formName}
            data={this.props.statesData}
            labelField={VARIABLES.State}
            valueField={VARIABLES.StateID}
            value={this.state.selectedStateFips}
            disabledValue={this.state.comparisonStateFips}
            onChange={value => this.setState({selectedStateFips: value})}
          />
        )}

        {this.props.showComparisonState && (
          <PlaceSelect
            label={TEXT.comparison_state + ':'}
            formId="comparisonStateFips"
            formName={this.props.formName}
            data={this.props.statesData}
            labelField={VARIABLES.State}
            valueField={VARIABLES.StateID}
            value={this.state.comparisonStateFips}
            disabledValue={this.state.selectedStateFips}
            onChange={value => this.setState({comparisonStateFips: value})}
          />
        )}

        {this.props.showSelectedCounty && (
          <PlaceSelect
            label={TEXT.selected_county + ':'}
            formId="selectedCountyFips"
            formName={this.props.formName}
            data={this.props.countiesData}
            labelField={VARIABLES.County}
            valueField={VARIABLES.CountyID}
            value={this.state.selectedCountyFips}
            disabledValue={this.state.comparisonCountyFips}
            onChange={value => this.setState({selectedCountyFips: value})}
          />
        )}

        {this.props.showComparisonCounty && (
          <PlaceSelect
            label={TEXT.comparison_county + ':'}
            formId="comparisonCountyFips"
            formName={this.props.formName}
            data={this.props.countiesData}
            labelField={VARIABLES.County}
            valueField={VARIABLES.CountyID}
            value={this.state.comparisonCountyFips}
            disabledValue={this.state.selectedCountyFips}
            onChange={value => this.setState({comparisonCountyFips: value})}
          />
        )}

        {this.props.showDisplayType && (
          <TypeSelect
            label={TEXT.display_type + ':'}
            formId="displayType"
            formName={this.props.formName}
            value={this.state.displayType}
            onChange={value => this.setState({displayType: value})}
          />
        )}

        {this.props.showSummaryType && (
          <SummarySelect
            label={TEXT.display_type + ':'}
            formId="summaryType"
            formName={this.props.formName}
            value={this.state.summaryType}
            onChange={value => this.setState({summaryType: value})}
          />
        )}

        <hr/>

        <Button
          id={this.props.formName + ' reset'}
          variant="link"
          type="button"
          size="sm"
          onClick={this.handleReset}>
          {TEXT.form_reset}
        </Button>

        <Button
          id={this.props.formName + ' submit'}
          className="float-right"
          variant="primary"
          type="submit"
          size="sm">
          {TEXT.form_submit}
        </Button>

      </Form>
    );
  }
}
